import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { GustoLogo } from "@/components/GustoLogo";
import gustoAvatar from "@/assets/gusto-avatar.png";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, Heart, AlertTriangle, Leaf, Fish,
  Egg, Milk, Wheat, Shell, TreePine, Check
} from "lucide-react";

const ALLERGENS = [
  { id: "gluten", label: "Gluten", icon: Wheat },
  { id: "lactose", label: "Lactose", icon: Milk },
  { id: "oeufs", label: "Oeufs", icon: Egg },
  { id: "fruits_mer", label: "Fruits de mer", icon: Shell },
  { id: "poisson", label: "Poisson", icon: Fish },
  { id: "arachides", label: "Arachides", icon: AlertTriangle },
  { id: "fruits_a_coque", label: "Fruits à coque", icon: TreePine },
];

const PREFERENCES = [
  { id: "bio", label: "Bio" },
  { id: "local", label: "Local" },
  { id: "de_saison", label: "De saison" },
  { id: "sans_sucre", label: "Sans sucre ajouté" },
  { id: "epice", label: "Épicé" },
  { id: "rapide", label: "Rapide (<30 min)" },
];

const DIET_TYPES = [
  { id: "omnivore", label: "Omnivore", emoji: "🍖" },
  { id: "vegetarien", label: "Végétarien", emoji: "🥕" },
  { id: "vegan", label: "Végan", emoji: "🌱" },
  { id: "pescetarien", label: "Pescétarien", emoji: "🐟" },
  { id: "flexitarien", label: "Flexitarien", emoji: "🥗" },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [dietType, setDietType] = useState("omnivore");
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [selectedPreferences, setSelectedPreferences] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleItem = (list, setList, item) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/dietary-profile`, {
        diet_type: dietType,
        allergies: selectedAllergens,
        preferences: selectedPreferences,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      toast.success("Profil alimentaire enregistré !");
      navigate("/");
    } catch {
      toast.error("Erreur, mais pas de souci, vous pourrez le faire plus tard !");
      navigate("/");
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    // Step 0: Welcome
    {
      content: (
        <div className="flex flex-col items-center text-center gap-6 py-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2, stiffness: 200 }}
          >
            <div className="w-28 h-28 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center shadow-xl ring-4 ring-white">
              <img src={gustoAvatar} alt="Gusto" className="w-20 h-20 object-contain" />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <h1 className="font-heading text-3xl font-bold text-charcoal">
              Bienvenue, Chef !
            </h1>
            <p className="text-muted-foreground mt-3 max-w-xs mx-auto leading-relaxed">
              Préparez-vous à vivre une aventure culinaire unique. Afin de vous régaler lors de vos prochains repas, dites-nous en un peu plus sur vous...
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
            <Button
              onClick={() => setStep(1)}
              className="rounded-full px-8 h-12 text-base gap-2 shadow-lg"
              data-testid="onboarding-start-btn"
            >
              C'est parti <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      ),
    },
    // Step 1: Diet type
    {
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-charcoal">Quel type de cuisine ?</h2>
            <p className="text-muted-foreground mt-1">Aidez-nous à personnaliser vos suggestions</p>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {DIET_TYPES.map((diet) => (
              <button
                key={diet.id}
                onClick={() => setDietType(diet.id)}
                className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${
                  dietType === diet.id
                    ? "border-primary bg-primary/5 shadow-md"
                    : "border-gray-100 hover:border-primary/30 bg-white"
                }`}
                data-testid={`diet-${diet.id}`}
              >
                <span className="text-3xl">{diet.emoji}</span>
                <span className="font-medium text-charcoal">{diet.label}</span>
                {dietType === diet.id && (
                  <Check className="w-5 h-5 text-primary ml-auto" />
                )}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    // Step 2: Allergens
    {
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <h2 className="font-heading text-2xl font-bold text-charcoal">Vos allergènes</h2>
            <p className="text-muted-foreground mt-1">Sélectionnez vos allergènes pour que vos amis cuisinent en sécurité</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {ALLERGENS.map((allergen) => {
              const Icon = allergen.icon;
              const selected = selectedAllergens.includes(allergen.id);
              return (
                <button
                  key={allergen.id}
                  onClick={() => toggleItem(selectedAllergens, setSelectedAllergens, allergen.id)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                    selected
                      ? "border-red-400 bg-red-50 shadow-sm"
                      : "border-gray-100 hover:border-red-200 bg-white"
                  }`}
                  data-testid={`allergen-${allergen.id}`}
                >
                  <Icon className={`w-5 h-5 ${selected ? "text-red-500" : "text-gray-400"}`} />
                  <span className={`text-sm font-medium ${selected ? "text-red-700" : "text-charcoal"}`}>
                    {allergen.label}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-center text-muted-foreground">
            Pas d'allergènes ? Passez à l'étape suivante
          </p>
        </div>
      ),
    },
    // Step 3: Preferences
    {
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <Heart className="w-8 h-8 text-primary mx-auto mb-2" />
            <h2 className="font-heading text-2xl font-bold text-charcoal">Vos goûts</h2>
            <p className="text-muted-foreground mt-1">Qu'est-ce qui vous fait vibrer en cuisine ?</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            {PREFERENCES.map((pref) => {
              const selected = selectedPreferences.includes(pref.id);
              return (
                <button
                  key={pref.id}
                  onClick={() => toggleItem(selectedPreferences, setSelectedPreferences, pref.id)}
                  className={`px-5 py-2.5 rounded-full border-2 transition-all text-sm font-medium ${
                    selected
                      ? "border-primary bg-primary text-white shadow-md"
                      : "border-gray-200 text-charcoal hover:border-primary/40 bg-white"
                  }`}
                  data-testid={`pref-${pref.id}`}
                >
                  {pref.label}
                </button>
              );
            })}
          </div>
        </div>
      ),
    },
    // Step 4: Finish
    {
      content: (
        <div className="flex flex-col items-center text-center gap-6 py-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-xl">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
          </motion.div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-charcoal">Vous êtes prêt(e) !</h2>
            <p className="text-muted-foreground mt-2 max-w-xs mx-auto">
              Votre profil gourmand est configuré. Chef Gusto va vous concocter des suggestions sur mesure.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {dietType !== "omnivore" && (
              <Badge className="bg-green-100 text-green-700">{DIET_TYPES.find(d => d.id === dietType)?.label}</Badge>
            )}
            {selectedAllergens.map(a => (
              <Badge key={a} className="bg-red-100 text-red-700">{ALLERGENS.find(x => x.id === a)?.label}</Badge>
            ))}
            {selectedPreferences.map(p => (
              <Badge key={p} className="bg-primary/10 text-primary">{PREFERENCES.find(x => x.id === p)?.label}</Badge>
            ))}
          </div>
          <Button
            onClick={handleFinish}
            disabled={saving}
            className="rounded-full px-8 h-12 text-base gap-2 shadow-lg"
            data-testid="onboarding-finish-btn"
          >
            {saving ? "Enregistrement..." : "Commencer l'aventure"}
            <Sparkles className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/80 to-white flex flex-col" data-testid="onboarding-page">
      {/* Header with logo */}
      <div className="text-center pt-6 pb-2">
        <GustoLogo size="md" className="mx-auto" />
      </div>

      {/* Progress */}
      {step > 0 && step < steps.length - 1 && (
        <div className="px-8 pt-2 max-w-md mx-auto w-full">
          <div className="flex gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  s <= step ? "bg-primary" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-right mt-1">{step}/3</p>
        </div>
      )}

      {/* Step Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3 }}
            >
              {steps[step].content}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {step > 0 && step < steps.length - 1 && (
            <div className="flex justify-between mt-8">
              <Button
                variant="ghost"
                onClick={() => setStep(step - 1)}
                className="rounded-full"
              >
                Retour
              </Button>
              <Button
                onClick={() => setStep(step + 1)}
                className="rounded-full gap-1"
              >
                Suivant <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Skip */}
          {step < steps.length - 1 && (
            <p className="text-center mt-4">
              <button
                onClick={() => navigate("/")}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                Passer et configurer plus tard
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
